import { DEFAULT_BUTTON_SIZE } from "./blocks/button-presentation";
import { createButtonBlock } from "./blocks/button";
import { createButtonRowBlock } from "./blocks/button-row";
import { createDividerBlock } from "./blocks/divider";
import {
  createEyebrowBlock,
  EYEBROW_FONT_SIZE,
  EYEBROW_TEXT_TRANSFORM,
} from "./blocks/eyebrow";
import { createImageBlock } from "./blocks/image";
import { createSocialBlock } from "./blocks/social";
import { createSpacerBlock } from "./blocks/spacer";
import { createTextBlock, TEXT_VARIANT_STYLE } from "./blocks/text";
import { ZERO_PADDING, ZERO_RADIUS } from "./create-shared";
import {
  columnContentGutter,
  createColumn,
  createGroup,
  createRow,
  defaultSettings,
} from "./factories";
import type { BorderStyle, CornerRadius, Padding, TextVariant } from "./types";

export type FieldReset = {
  dirty: boolean;
  onReset: () => void;
};

export function baselineEmailSettings() {
  return defaultSettings();
}

export function baselineRowColumnGap(columnCount: number): number {
  return columnCount > 1 ? 16 : 0;
}

export function baselineRowPadding(nestedInGroup = false) {
  return createRow("one", undefined, { nestedInGroup }).padding;
}

export function baselineGroupedRowPadding() {
  return baselineRowPadding(true);
}

export function baselineRowBackgroundColor() {
  return createRow("one").backgroundColor;
}

export function baselineRowBorder(): BorderStyle {
  return createRow("one").border;
}

export function baselineRowBorderRadius(): CornerRadius {
  return createRow("one").borderRadius ?? ZERO_RADIUS;
}

export function baselineGroupPadding() {
  return createGroup().padding;
}

export function baselineGroupBackgroundColor() {
  return createGroup().backgroundColor;
}

export function baselineGroupBorder(): BorderStyle {
  return createGroup().border;
}

export function baselineGroupBorderRadius(): CornerRadius {
  return createGroup().borderRadius ?? ZERO_RADIUS;
}

export function baselineColumnPadding(
  columnIndex: number,
  columnCount: number,
): Padding {
  const gutter = columnContentGutter(columnIndex, columnCount);
  return { top: 0, right: gutter.right, bottom: 0, left: gutter.left };
}

export function baselineColumnBackgroundColor() {
  return createColumn().backgroundColor;
}

export function baselineColumnBorder(): BorderStyle {
  return createColumn().border;
}

export function baselineColumnBorderRadius(): CornerRadius {
  return createColumn().borderRadius ?? ZERO_RADIUS;
}

export function baselineTextTypography(variant: TextVariant) {
  return TEXT_VARIANT_STYLE[variant];
}

export function baselineTextPadding() {
  return { ...createTextBlock().padding };
}

export function baselineButtonSize() {
  return createButtonBlock().size;
}

export function baselineButtonPadding() {
  return createButtonBlock().padding;
}

export function baselineButtonBorderRadius(): CornerRadius {
  return createButtonBlock().borderRadius ?? ZERO_RADIUS;
}

export function baselineImageBorderRadius(): CornerRadius {
  return createImageBlock().borderRadius ?? ZERO_RADIUS;
}

export function baselineImagePadding() {
  return createImageBlock().padding;
}

export function baselineDividerWidthPct() {
  return createDividerBlock().widthPct;
}

export function baselineDividerPadding() {
  return createDividerBlock().padding;
}

export function baselineDividerThickness() {
  return createDividerBlock().thickness;
}

export function baselineSocialIconSize() {
  return createSocialBlock().iconSize;
}

export function baselineSocialGap() {
  return createSocialBlock().gap;
}

export function baselineSocialPadding() {
  return createSocialBlock().padding;
}

export function baselineEyebrowPadding() {
  return createEyebrowBlock().padding;
}

export function baselineEyebrowFontSize() {
  return createEyebrowBlock().fontSize ?? EYEBROW_FONT_SIZE;
}

export function baselineEyebrowTextTransform() {
  return createEyebrowBlock().textTransform ?? EYEBROW_TEXT_TRANSFORM;
}

export function baselineButtonRowGap() {
  return createButtonRowBlock().gap;
}

export function baselineButtonRowPadding() {
  return createButtonRowBlock().padding;
}

export function baselineButtonRowSlotSize(index: number) {
  return createButtonRowBlock().buttons[index]?.size ?? DEFAULT_BUTTON_SIZE;
}

export function baselineButtonRowSlotBorderRadius(index: number): CornerRadius {
  return (
    createButtonRowBlock().buttons[index]?.borderRadius ??
    createButtonRowBlock().buttons[0].borderRadius
  );
}

export function baselineSpacerHeight() {
  return createSpacerBlock().height;
}

export function baselinePagePadding() {
  return { ...ZERO_PADDING };
}
